package com.evoapps.evoshift.domain.model

/**
 * What a person may do at a workplace. The single source for navigation, screens and buttons:
 * the app shows only what the role may use, so nobody meets "you don't have permission".
 *
 * Mirrors firestore.rules (the server is the authority): "any member" = isEmployee, "managers"
 * = isManager (owner, financial and operational managers), "finances" = isFinancialManager
 * (owner and financial managers), "owner" = the workplace's owner. The e2e suite checks both
 * directions: what a role is shown works, what it is not shown is denied.
 */
enum class Capability {
    // --- Any member of the workplace ---
    /** Own schedule, the marketplace, swap requests, availability, absences, clocking in. */
    WORK_SHIFTS,

    // --- Managers (owner, financial and operational) ---
    /** Rosters: create, build, publish, delete; shifts, templates, the weekly skeleton. */
    EDIT_ROSTER,
    /** Swap and absence requests to approve or reject. */
    APPROVE_REQUESTS,
    /** The team list, inviting people, editing their role and skills. */
    MANAGE_TEAM,
    /** Availability campaigns and reading the team's availability. */
    REQUEST_AVAILABILITY,
    /** The workplace's general settings, location, roles, slots, swap policy. */
    EDIT_WORKPLACE_SETTINGS,
    /** Working-time reports (ewidencja czasu pracy). */
    PRINT_WORKING_TIME,

    // --- Finances (owner and financial managers) ---
    /** Money: labour cost, revenue, Daily Records, the Financials tab, dashboard money figures. */
    VIEW_FINANCES,
    /** Pay rates and personal data of the team (sensitive), adding people with their pay. */
    MANAGE_PAY,
    /** Payroll, payslips, bonuses and deductions. */
    PAYROLL,
    /** Workplace financial settings (fixed revenue, commission, sales targets, payroll settings). */
    EDIT_WORKPLACE_FINANCES,

    // --- The owner ---
    /** Recurring costs (the owner's own rules). */
    MANAGE_COST_RULES,
    /** Deleting the workplace. */
    DELETE_WORKPLACE,
    /** The subscription (billing entity). */
    BILLING,

    // --- The workplace's plan ---
    /**
     * Changing the workplace's data at all (SH-65): every role has it while the workplace's plan
     * runs; once it ended ([withPlan]) everything stays visible and nothing can be changed until
     * the owner chooses a plan. firestore.rules (planAllowsChanges) refuse the writes too.
     */
    MAKE_CHANGES
}

/** What [this] role may do. An inactive engagement may do nothing (see [Engagement.capabilities]). */
fun UserPermission.can(capability: Capability): Boolean = when (capability) {
    Capability.WORK_SHIFTS, Capability.MAKE_CHANGES -> true
    Capability.EDIT_ROSTER,
    Capability.APPROVE_REQUESTS,
    Capability.MANAGE_TEAM,
    Capability.REQUEST_AVAILABILITY,
    Capability.EDIT_WORKPLACE_SETTINGS,
    Capability.PRINT_WORKING_TIME -> this != UserPermission.EMPLOYEE
    Capability.VIEW_FINANCES,
    Capability.MANAGE_PAY,
    Capability.PAYROLL,
    Capability.EDIT_WORKPLACE_FINANCES -> this == UserPermission.OWNER || this == UserPermission.MANAGER_FINANCIAL
    Capability.MANAGE_COST_RULES,
    Capability.DELETE_WORKPLACE,
    Capability.BILLING -> this == UserPermission.OWNER
}

/** Everything [this] role may do. */
val UserPermission.capabilities: Set<Capability>
    get() = Capability.entries.filterTo(mutableSetOf()) { can(it) }

/**
 * SH-270: the feature level a capability needs ([Feature], [AccessLevel]); null for those that are
 * not a feature: every member's own work and the plan ([Capability.WORK_SHIFTS],
 * [Capability.MAKE_CHANGES]) and the owner's ([Capability.MANAGE_COST_RULES],
 * [Capability.DELETE_WORKPLACE], [Capability.BILLING], decided by the permission as before).
 * Built-in roles give every capability exactly as [UserPermission.can] does (CapabilityTest).
 */
val Capability.feature: Pair<Feature, AccessLevel>?
    get() = when (this) {
        Capability.WORK_SHIFTS, Capability.MAKE_CHANGES,
        Capability.MANAGE_COST_RULES, Capability.DELETE_WORKPLACE, Capability.BILLING -> null
        Capability.EDIT_ROSTER -> Feature.ROSTER to AccessLevel.EDIT
        Capability.APPROVE_REQUESTS -> Feature.REQUESTS to AccessLevel.EDIT
        Capability.MANAGE_TEAM -> Feature.TEAM to AccessLevel.EDIT
        Capability.REQUEST_AVAILABILITY -> Feature.AVAILABILITY to AccessLevel.EDIT
        Capability.EDIT_WORKPLACE_SETTINGS -> Feature.SETTINGS to AccessLevel.EDIT
        Capability.PRINT_WORKING_TIME -> Feature.REPORTS to AccessLevel.VIEW
        Capability.VIEW_FINANCES -> Feature.FINANCES to AccessLevel.VIEW
        Capability.MANAGE_PAY -> Feature.PAY to AccessLevel.EDIT
        Capability.PAYROLL -> Feature.PAY to AccessLevel.VIEW
        Capability.EDIT_WORKPLACE_FINANCES -> Feature.FINANCES to AccessLevel.EDIT
    }

/**
 * What the person may do through this engagement: nothing once it ended (the rules require
 * `isActive`), else what its feature levels allow (SH-270: the role and the person's
 * modifications, [levelOf]); the owner's own capabilities by the permission.
 */
val Engagement.capabilities: Set<Capability>
    get() = if (!isActive) emptySet() else Capability.entries.filterTo(mutableSetOf()) { capability ->
        val needed = capability.feature
        if (needed == null) permission.can(capability) else levelOf(needed.first).value >= needed.second.value
    }

/** What [this] person may do at [workplaceId]; nothing without an active engagement there. */
fun User.capabilitiesAt(workplaceId: String?): Set<Capability> =
    workplaceId?.let { engagements[it] }?.capabilities.orEmpty()

/**
 * [this] as the workplace's plan leaves it (SH-65): all of it while the plan is active, without
 * [Capability.MAKE_CHANGES] once it ended (read-only: everything to see, nothing to change).
 */
fun Set<Capability>.withPlan(planActive: Boolean): Set<Capability> =
    if (planActive) this else this - Capability.MAKE_CHANGES

/** What [this] person may do at [workplaceId] while its plan is [planActive] or not (see [withPlan]). */
fun User.capabilitiesAt(workplaceId: String?, planActive: Boolean): Set<Capability> =
    capabilitiesAt(workplaceId).withPlan(planActive)

/** Whether [this] are read-only: the person works there, but the workplace's plan ended (SH-65). */
val Set<Capability>.isReadOnly: Boolean
    get() = isNotEmpty() && Capability.MAKE_CHANGES !in this

/** What [this] person may do at any of their workplaces (the profile: the plan, cost rules). */
val User.capabilitiesAnywhere: Set<Capability>
    get() = engagements.values.flatMapTo(mutableSetOf()) { it.capabilities }

/**
 * Whether [this] role may change the engagement of someone who has (or is to get) [permission]:
 * nobody above their own level. firestore.rules (mayWriteEngagement) checks the permission a
 * write leaves; the app also keeps managers off the engagements of people above them.
 * Employees change nobody's.
 */
fun UserPermission.mayAssign(permission: UserPermission): Boolean = when (this) {
    UserPermission.OWNER -> true
    UserPermission.MANAGER_FINANCIAL -> permission != UserPermission.OWNER
    UserPermission.MANAGER -> permission == UserPermission.MANAGER || permission == UserPermission.EMPLOYEE
    UserPermission.EMPLOYEE -> false
}

/**
 * The workplace to open when nothing is remembered: the person's primary active engagement, else
 * the active one they have had longest (then by name/id, so the choice is stable); null without any.
 */
fun User.defaultWorkplaceId(): String? =
    engagements.values.filter { it.isActive }
        .sortedWith(compareByDescending<Engagement> { it.isPrimary }.thenBy { it.startDate }.thenBy { it.cachedWorkplaceName.orEmpty() }.thenBy { it.workplaceId })
        .firstOrNull()?.workplaceId
